import React from "react"
import {useCookies} from "react-cookie"
import {useTranslation} from "react-i18next"
import {makeStyles} from "@material-ui/core/styles"
import {IconButton} from "@material-ui/core"
import TranslateIcon from "@material-ui/icons/Translate"
import {useDispatch} from "../Store"
import {getCookieOptions} from "../common"
import TimelineIcon from "@material-ui/icons/Timeline"
import AnnouncementIcon from "@material-ui/icons/Announcement"
import LiveHelpIcon from "@material-ui/icons/LiveHelp"
import EqualizerIcon from "@material-ui/icons/Equalizer"
import InvertColorsIcon from "@material-ui/icons/InvertColors"
import LogoDarkMode from "../../assets/images/logo/logoDarkMode.svg"
import LogoLightMode from "../../assets/images/logo/logoLightMode.svg"
import LogoFavicon from "../../assets/images/favicon.svg"
import clsx from "clsx"
import {useStore} from "../Store"
import {useMediaQuery} from "@material-ui/core"
import {useTheme} from "../hooks"

const HOME_URL = process.env.HOME_URL

const useStyles = makeStyles((theme) => ({
  title: {
    textAlign: `left`,
    float: `left`,
    display: `inline-block`,
  },
  icon: {
    paddingTop: (props) => (props.stepsEnabled ? 10 : 0),
    textAlign: `right`,
    marginRight: (props) => {
      if (props.preset === `createDefaultLayout`) return props.collapsed ? 0 : 0
      if (props.preset === `createStandardLayout`) return props.collapsed ? 0 : 0
      if (props.preset === `createFixedLayout`) return props.collapsed ? (props.isMobile ? 0 : 50) : props.isMobile ? 0 : 250
      if (props.preset === `createContentBasedLayout`) return props.collapsed ? 0 : 0
      if (props.preset === `createCozyLayout`) return props.collapsed ? 0 : 0
      if (props.preset === `createMuiTreasuryLayout`) return props.collapsed ? 0 : 0
    },
    width: (props) => {
      if (props.preset === `createDefaultLayout`) return `75%`
      if (props.preset === `createStandardLayout`) return `75%`
      if (props.preset === `createFixedLayout`) return `75%`
      if (props.preset === `createContentBasedLayout`) return `75%`
      if (props.preset === `createCozyLayout`) return `80%`
      if (props.preset === `createMuiTreasuryLayout`) return `78%`
    },
  },
  app: {
    fontWeight: 900,
    minWidth: 0,
    fontSize: `2rem`,
    [theme.breakpoints.down(`xm`)]: {
      display: `none`,
    },
  },
  logo: {
    height: (props) => (props.isMobile ? 36 : 40),
    margin: (props) => (props.isMobile ? `5px 0px 5px 0px` : `5px 20px 5px 0px`),
  },
  trendIcon: {
    zIndex: (props) => (props.zIndex === `trendIcon` ? 2201 : 0),
  },
  rankingIcon: {
    zIndex: (props) => (props.zIndex === `rankingIcon` ? 2201 : 0),
  },
}))

export default function HeaderEx() {
  const {
    store: {zIndex, stepsEnabled, preset, collapsed, themeColor, locale, guideInfo, tabs, trendPanel, rankingPanel, page},
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const {dispatch} = useDispatch()
  const classes = useStyles({
    preset,
    collapsed,
    isMobile,
    stepsEnabled,
    zIndex,
  })
  const {i18n} = useTranslation()
  const [, setCookie] = useCookies([`milizeLocale`, `milizeThemeColor`])

  const trendPanelIcon = () => {
    dispatch({
      type: `TREND_PANEL`,
      trendPanel: !trendPanel,
    })
  }

  const rankingPanelIcon = () => {
    dispatch({
      type: `RANKING_PAGE`,
      rankingPage: 1,
    })
    dispatch({
      type: `RANKING_PANEL`,
      rankingPanel: !rankingPanel,
    })
  }

  const guideTour = async () => {
    dispatch({
      type: `GUIDE_HINTS`,
      hintsEnabled: false,
    })
    dispatch({
      type: `PAGE`,
      page: `TREND_RANKING`,
    })
    dispatch({
      type: `PERSPECTIVE`,
      perspective: 0, // First page
    })
    await new Promise((resolve) => setTimeout(resolve, 100))
    dispatch({
      type: `GUIDE_STEPS`,
      stepsEnabled: !guideInfo.stepsEnabled,
    })
  }

  const guideHint = async () => {
    dispatch({
      type: `GUIDE_STEPS`,
      stepsEnabled: false,
    })
    await new Promise((resolve) => setTimeout(resolve, 100))
    dispatch({
      type: `GUIDE_HINTS`,
      hintsEnabled: !guideInfo.hintsEnabled,
    })
  }

  const changeThemeColor = (themeColor) => () => {
    const color = themeColor === `dark` ? `light` : `dark`
    dispatch({
      type: `THEME_COLOR`,
      themeColor: color,
    })
    setCookie(`milizeThemeColor`, color, getCookieOptions())
  }

  const changeLanguage = (locale, i18n) => async () => {
    const lang = locale === `ja` ? `en` : `ja`
    dispatch({
      type: `LOCALE`,
      locale: lang,
    })
    setCookie(`milizeLocale`, lang, getCookieOptions())
    await i18n.changeLanguage(lang)
  }

  const logo = isMobile ? (
    <a href={HOME_URL} target={`_blank`}>
      <LogoFavicon width={32} />
    </a>
  ) : (
    <a href={HOME_URL} target={`_blank`}>
      {themeColor === `dark` ? <LogoDarkMode width={256} /> : <LogoLightMode width={256} />}
    </a>
  )

  return (
    <>
      {logo}
      <div className={classes.icon}>
        {page === 'SIMILAR_CHART' && (
          <IconButton title={``} className={clsx(classes.trendIcon, `trendIcon`)} onClick={trendPanelIcon}>
            <TimelineIcon />
          </IconButton>
        )}
        {page === 'SIMILAR_CHART' && (
          <IconButton title={``} className={clsx(classes.rankingIcon, `rankingIcon`)} onClick={rankingPanelIcon}>
            <EqualizerIcon />
          </IconButton>
        )}
        <IconButton title={``} onClick={() => guideTour()}>
          <AnnouncementIcon />
        </IconButton>
        <IconButton title={``} onClick={() => guideHint()}>
          <LiveHelpIcon />
        </IconButton>
        {!isMobile && (
          <IconButton title={``} onClick={changeThemeColor(themeColor)}>
            <InvertColorsIcon />
          </IconButton>
        )}
        {!isMobile && (
          <IconButton title={``} onClick={changeLanguage(locale, i18n)}>
            <TranslateIcon />
          </IconButton>
        )}
      </div>
    </>
  )
}
